import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components';
import { Button, Checkbox, Error, Field, Select } from '@/components/generic';
import { SelectOption } from '@/types/select';
import {
  useGetAreaCodes,
  useGetCities,
  useGetRegions,
  useUploadFile,
} from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/* Schemas */
export const RegisterOptionalDataFormSchema = z
  .object({
    cardTypeId: z.string().min(1, { message: 'Card Type is required' }),
    cardHolderModel: z.enum(['B2B', 'B2C']).optional(),
    firstName: z.string().min(1, { message: 'First Name is required' }),
    lastName: z.string().min(1, { message: 'Last Name is required' }),
    areaCode: z.string().min(1, { message: 'Area Code is required' }),
    mobile: z
      .string()
      .min(1, { message: 'Mobile number is required' })
      .refine((value) => /^\d+$/.test(value), {
        message: 'Invalid mobile number',
      }),
    // Required for both B2B (virtual) and B2C (physical) card holders
    gender: z.enum(['M', 'F'], {
      required_error: 'Gender is required',
      invalid_type_error: 'Gender is required',
    }),
    // B2C fields
    birthday: z.string().optional(),
    address: z.string().optional(),
    postCode: z.string().optional(),
    town: z.string().optional(),
    nationality: z.string().optional(),
    idType: z
      .enum(['PASSPORT', 'HK_HKID', 'DLN', 'GOVERNMENT_ISSUED_ID_CARD'])
      .optional(),
    idNumber: z
      .string()
      .min(2, { message: 'ID Number must be at least 2 characters' })
      .max(50, { message: 'ID Number must be at most 50 characters' })
      .regex(/^[a-zA-Z0-9\-\s]+$/, {
        message: 'ID Number can only contain letters, numbers, hyphens and spaces',
      })
      .optional(),
    issueDate: z.string().optional(),
    idNoExpiryDate: z.string().optional(),
    idFrontId: z.string().optional(),
    idBackId: z.string().optional(),
    idHoldId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.cardHolderModel !== 'B2C') return;
    const b2cRequired = [
      'birthday',
      'address',
      'postCode',
      'town',
      'nationality',
      'idType',
      'idNumber',
      'issueDate',
      'idNoExpiryDate',
      'idFrontId',
      'idBackId',
      'idHoldId',
    ] as const;
    for (const field of b2cRequired) {
      if (!data[field]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Required for physical card',
        });
      }
    }
    for (const field of ['birthday', 'issueDate', 'idNoExpiryDate'] as const) {
      if (data[field] && !DATE_REGEX.test(data[field]!)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Must be in YYYY-MM-DD format',
        });
      }
    }
  });

/* Types */
export type RegisterOptionalDataFormType = z.infer<
  typeof RegisterOptionalDataFormSchema
>;

/* Internal component — file upload field */
const FileUploadField = ({
  label,
  onUploaded,
  error,
}: {
  label: string;
  onUploaded: (fileId: string) => void;
  error?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  const uploadFile = useUploadFile({
    onSuccess: (data: any) => {
      onUploaded(data.data.fileId);
      setUploaded(true);
      setUploading(false);
    },
    onError: () => {
      setUploading(false);
    },
  });

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPG and PNG files are allowed.');
      e.target.value = '';
      return;
    }
    const maxSizeBytes = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSizeBytes) {
      alert('File size must not exceed 5 MB.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    setUploaded(false);
    await uploadFile.mutateAsync({ file });
  };

  return (
    <div className='flex flex-col gap-1'>
      <span className='text-body-2 font-medium text-t-primary'>{label}</span>
      <div className='flex items-center gap-2'>
        <input
          ref={inputRef}
          type='file'
          accept='image/jpeg,image/png'
          className='hidden'
          onChange={handleChange}
        />
        <Button
          type='button'
          isStroke
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? 'Uploading...' : uploaded ? 'Replace' : 'Choose file'}
        </Button>
        {uploaded && (
          <span className='text-body-2 text-green-500'>Uploaded ✓</span>
        )}
      </div>
      {error && <p className='text-body-2 text-red-500'>{error}</p>}
    </div>
  );
};

/* Props */
type CardHolderFormProps = {
  mode: 'create' | 'edit';
  /** Card type is not editable; injected into the payload. */
  cardTypeId: string;
  /** Prefill values (edit mode). `country` seeds the Country dropdown
   * (regionCode), which is local state rather than a form field. */
  initialValues?: Partial<RegisterOptionalDataFormType> & { country?: string };
  onSubmit: (
    data: RegisterOptionalDataFormType & { country?: string },
  ) => void | Promise<void>;
  submitLabel: string;
  isSubmitting?: boolean;
  error?: string;
  /** Cancel button (create modal). */
  onCancel?: () => void;
  /** Terms checkbox gating submit (create only). */
  showTerms?: boolean;
};

/* Shared card holder form — used by create (CreateNewCardHolder) and edit
 * (CardHolderManager). Always B2C (physical) so the full KYC set is required. */
export const CardHolderForm = ({
  mode,
  cardTypeId,
  initialValues,
  onSubmit,
  submitLabel,
  isSubmitting,
  error,
  onCancel,
  showTerms,
}: CardHolderFormProps) => {
  const cardHolderModel: 'B2B' | 'B2C' = 'B2C';
  const isB2C = cardHolderModel === 'B2C';
  const [terms, setTerms] = useState(false);
  const [regionCode, setRegionCode] = useState(initialValues?.country ?? '');

  // `country` is not a form field (Country drives regionCode); keep it out of
  // the react-hook-form values.
  const { country: _country, ...initialFormValues } = initialValues ?? {};

  const form = useForm<RegisterOptionalDataFormType>({
    resolver: zodResolver(RegisterOptionalDataFormSchema),
    defaultValues: {
      cardTypeId,
      cardHolderModel,
      ...initialFormValues,
    },
  });

  /* Effects — prefill on edit when initialValues arrive/change */
  useEffect(() => {
    if (mode !== 'edit' || !initialValues) return;
    form.reset({ cardTypeId, cardHolderModel, ...initialFormValues });
    setRegionCode(initialValues.country ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, cardTypeId]);

  /* Queries */
  const { data: areaCodes } = useGetAreaCodes({
    onError: (err: Error) => {
      console.error(`Get Area Codes: Axios error occurred - ${err.message}`);
    },
  });
  const { data: regions } = useGetRegions({
    onError: (err: Error) => {
      console.error(`Get Regions: Axios error occurred - ${err.message}`);
    },
  });
  const { data: cities } = useGetCities({
    regionCode,
    enabled: isB2C && regionCode !== '',
    onError: (err: Error) => {
      console.error(`Get Cities: Axios error occurred - ${err.message}`);
    },
  });

  /* Callbacks */
  const handleSubmit = useCallback(
    async (data: RegisterOptionalDataFormType) => {
      try {
        await onSubmit({
          ...data,
          ...(regionCode ? { country: regionCode } : {}),
        });
      } catch (err) {
        console.error('Error submitting form:', err);
      }
    },
    [onSubmit, regionCode],
  );

  /* Memos */
  const mobileAreaCodes = useMemo(
    (): SelectOption[] =>
      areaCodes
        ?.filter((areaCode) => areaCode.name !== 'Europe')
        ?.sort((a, b) => a.code.localeCompare(b.code))
        ?.map((areaCode, index) => ({
          id: index,
          name:
            areaCode.code +
            ' ' +
            (areaCode.name === 'Canada' ? 'USA/Canada' : areaCode.name),
          value: areaCode.code,
        })) || [],
    [areaCodes],
  );
  const formFields = useMemo(
    () => [
      { name: 'firstName', label: 'First Name', type: 'input' },
      { name: 'lastName', label: 'Last Name', type: 'input' },
      {
        name: 'areaCode',
        label: 'Area Code',
        type: 'dropdown',
        options: mobileAreaCodes,
      },
      { name: 'mobile', label: 'Mobile', type: 'input' },
    ],
    [mobileAreaCodes],
  );
  const countries = useMemo(
    (): SelectOption[] =>
      regions
        ?.map((region: any, index: number) => ({
          id: index,
          name: region.name,
          value: region.code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) || [],
    [regions],
  );
  const townOptions = useMemo(
    (): SelectOption[] =>
      cities?.map((city: any, index: number) => ({
        id: index,
        name: city.name,
        value: city.code,
      })) || [],
    [cities],
  );
  const idTypeOptions: SelectOption[] = [
    { id: 0, name: 'Passport', value: 'PASSPORT' },
    { id: 1, name: 'HK Identity Card', value: 'HK_HKID' },
    { id: 2, name: "Driver's License", value: 'DLN' },
    { id: 3, name: 'Government-issued ID', value: 'GOVERNMENT_ISSUED_ID_CARD' },
  ];
  const genderOptions: SelectOption[] = [
    { id: 0, name: 'Male', value: 'M' },
    { id: 1, name: 'Female', value: 'F' },
  ];

  const submitDisabled = (showTerms && !terms) || !!isSubmitting;

  return (
    <Form {...form}>
      <form
        className='flex flex-col gap-3'
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        {formFields.map(({ name, label, type, options }) => (
          <FormField
            key={name}
            control={form.control}
            name={name as keyof RegisterOptionalDataFormType}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  {type === 'dropdown' ? (
                    <Select
                      label={label}
                      value={
                        options?.find((opt) => opt?.value === field.value) ||
                        null
                      }
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption.value)
                      }
                      options={options || []}
                      placeholder={`Select ${label}`}
                    />
                  ) : (
                    <Field
                      label={label}
                      {...field}
                      type={type}
                      placeholder={label}
                    />
                  )}
                </FormControl>
                <FormMessage className='text-start' />
              </FormItem>
            )}
          />
        ))}

        <FormField
          control={form.control}
          name='gender'
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Select
                  label='Gender'
                  value={
                    genderOptions.find((opt) => opt.value === field.value) ||
                    null
                  }
                  onChange={(opt) => field.onChange(opt.value)}
                  options={genderOptions}
                  placeholder='Select Gender'
                />
              </FormControl>
              <FormMessage className='text-start' />
            </FormItem>
          )}
        />

        {isB2C && (
          <>
            <Select
              label='Country'
              value={countries.find((opt) => opt.value === regionCode) || null}
              onChange={(opt) => setRegionCode(opt.value)}
              options={countries}
              placeholder='Select Country'
            />
            <FormField
              control={form.control}
              name='nationality'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      label='Nationality'
                      value={
                        countries.find((opt) => opt.value === field.value) ||
                        null
                      }
                      onChange={(opt) => field.onChange(opt.value)}
                      options={countries}
                      placeholder='Select Nationality'
                    />
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />
            {regionCode && (
              <FormField
                control={form.control}
                name='town'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select
                        label='Town'
                        value={
                          townOptions.find((opt) => opt.value === field.value) ||
                          null
                        }
                        onChange={(opt) => field.onChange(opt.value)}
                        options={townOptions}
                        placeholder='Select Town'
                      />
                    </FormControl>
                    <FormMessage className='text-start' />
                  </FormItem>
                )}
              />
            )}
            {(
              [
                { name: 'address', label: 'Address', placeholder: 'Address' },
                {
                  name: 'postCode',
                  label: 'Post Code',
                  placeholder: 'Post Code',
                },
              ] as const
            ).map(({ name, label, placeholder }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Field
                        label={label}
                        {...field}
                        type='input'
                        placeholder={placeholder}
                      />
                    </FormControl>
                    <FormMessage className='text-start' />
                  </FormItem>
                )}
              />
            ))}
            <FormField
              control={form.control}
              name='birthday'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Field
                      label='Birthday'
                      {...field}
                      type='date'
                      placeholder='YYYY-MM-DD'
                    />
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='idType'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      label='ID Type'
                      value={
                        idTypeOptions.find((opt) => opt.value === field.value) ||
                        null
                      }
                      onChange={(opt) => field.onChange(opt.value)}
                      options={idTypeOptions}
                      placeholder='Select ID Type'
                    />
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='idNumber'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Field
                      label='ID Number'
                      {...field}
                      type='input'
                      placeholder='ID Number'
                      maxLength={50}
                    />
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='issueDate'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Field
                      label='Issue Date'
                      {...field}
                      type='date'
                      placeholder='YYYY-MM-DD'
                    />
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='idNoExpiryDate'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Field
                      label='Expiry Date'
                      {...field}
                      type='date'
                      placeholder='YYYY-MM-DD'
                    />
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />
            <div className='flex gap-2 justify-between w-full'>
              <FileUploadField
                label='ID Front Photo'
                onUploaded={(id) => form.setValue('idFrontId', id)}
                error={form.formState.errors.idFrontId?.message}
              />
              <FileUploadField
                label='ID Back Photo'
                onUploaded={(id) => form.setValue('idBackId', id)}
                error={form.formState.errors.idBackId?.message}
              />
              <FileUploadField
                label='Selfie / Face Photo'
                onUploaded={(id) => form.setValue('idHoldId', id)}
                error={form.formState.errors.idHoldId?.message}
              />
            </div>
          </>
        )}

        {showTerms && (
          <Checkbox
            label='I understand that I am creating a new card holder and I need to have sufficient balance in my account to cover the card issuance fee.'
            checked={terms}
            onChange={(checked) => setTerms(checked)}
            required
            name='terms'
            id='terms'
            value='terms'
          />
        )}
        <Error error={error} message={error || 'Error saving card holder'} />

        <div className='flex justify-end gap-3 mt-8'>
          {onCancel && (
            <Button
              className='flex-1'
              isStroke
              type='button'
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button
            className='flex-1'
            isBlack={!showTerms || terms}
            isWhite={showTerms && !terms}
            type='submit'
            disabled={submitDisabled}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};
